import { combineReducers, configureStore, type AnyAction } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { setupListeners } from "@reduxjs/toolkit/query";
import authReducer, { logout } from "./slices/authSlice";

// Filter feature slices
import companyReducer from "./slices/companySlice";
import commonSearchReducer from "./slices/commonSearchSlice";
import peopleReducer from "./slices/peopleSlice";
import enrichmentProgressReducer from "./slices/enrichmentProgressSlice";
import workflowProgressReducer from "./slices/workflowProgressSlice";
import phoneWorkflowProgressReducer from "./slices/phoneWorkflowProgressSlice";
import radarBulkUnlockReducer from "./slices/radarBulkUnlockSlice";
import bulkBatchesReducer from "./slices/bulkBatchesSlice";
import workspaceColumnsReducer from "./slices/workspaceColumnsSlice";
import workspaceDataReducer from "./slices/workspaceDataSlice";
import workspaceEmailReducer from "./slices/workspaceEmailSlice";
import workspacePhoneReducer from "./slices/workspacePhoneSlice";
import workspaceSearchReducer from "./slices/workspaceSearchSlice";
import workspaceUIReducer from "./slices/workspaceUISlice";

// Filter feature RTK Query APIs
import { companyApi } from "./api/companyApi";
import { peopleApi } from "./api/peopleApi";
import { searchApi } from "./api/searchApi";
import { exportApi } from "./api/exportApi";
import { creditApi } from "./api/creditApi";
import { entitlementsApi } from "./api/entitlementsApi";
import { teamApi } from "./api/teamApi";
import { profileApi } from "./api/profileApi";
import { pricingApi } from "./api/pricingApi";
import { dropdownApi } from "./api/dropdownApi";
import { adminApi } from "./api/adminApi";
import { enrichmentApi } from "./api/enrichmentApi";
import { workspaceApi } from "./api/workspaceApi";
import { uploadsApi } from "./api/uploadsApi";
import { notificationApi } from "./api/notificationApi";
import { companyProfileApi } from "./api/companyProfileApi";
import { savedCompaniesApi } from "./api/savedCompaniesApi";
import { savedContactsApi } from "./api/savedContactsApi";
import { workflowApi } from "./api/workflowApi";
import { phoneWorkflowApi } from "./api/phoneWorkflowApi";
import { providersApi } from "./api/providersApi";
import {
  emailSuppressionApi,
  nameSuppressionApi,
  websiteSuppressionApi,
} from "./api/listApi";
import { contactListApi } from "./api/contactListApi";
import { crmApi } from "./api/crmApi";
import { crmImportApi } from "./api/crmImportApi";
import { hubspotApi } from "./api/hubspotApi";
import { salesforceApi } from "./api/salesforceApi";
import { customDataApi } from "./api/customDataApi";

/**
 * Every slice and every RTK Query cache, wrapped so a logout can wipe all of
 * it in one place.
 *
 * The reference implementation never needed this: its logout does a hard
 * `window.location.href` navigation, which destroys the whole JS process and
 * starts the next login from nothing. Ours is a pure client-side transition —
 * AuthGuard reacts to `status` alone, nothing ever reloads the page — so
 * every RTK Query cache (credit balance, entitlements, team, saved
 * contacts/companies, 20 others) survives logout untouched. Sign out, sign
 * back in as someone else within any cache's `keepUnusedDataFor` window (5
 * minutes for entitlements), and the new session briefly renders the PREVIOUS
 * account's plan, balance and permissions.
 */
const appReducer = combineReducers({
  auth: authReducer,
    // Filter feature slices
    company: companyReducer,
    commonSearch: commonSearchReducer,
    people: peopleReducer,
    enrichmentProgress: enrichmentProgressReducer,
    workflowProgress: workflowProgressReducer,
    phoneWorkflowProgress: phoneWorkflowProgressReducer,
    radarBulkUnlock: radarBulkUnlockReducer,
    bulkBatches: bulkBatchesReducer,
    workspaceColumns: workspaceColumnsReducer,
    workspaceData: workspaceDataReducer,
    workspaceEmail: workspaceEmailReducer,
    workspacePhone: workspacePhoneReducer,
    workspaceSearch: workspaceSearchReducer,
    workspaceUI: workspaceUIReducer,
    // Filter feature RTK Query reducers
    [companyApi.reducerPath]: companyApi.reducer,
    [peopleApi.reducerPath]: peopleApi.reducer,
    [searchApi.reducerPath]: searchApi.reducer,
    [exportApi.reducerPath]: exportApi.reducer,
    [creditApi.reducerPath]: creditApi.reducer,
    [entitlementsApi.reducerPath]: entitlementsApi.reducer,
    [teamApi.reducerPath]: teamApi.reducer,
    [profileApi.reducerPath]: profileApi.reducer,
    [pricingApi.reducerPath]: pricingApi.reducer,
    [dropdownApi.reducerPath]: dropdownApi.reducer,
    // Internal super-admin console. Registered unconditionally — the flag decides
    // what RENDERS, not what exists in the store, and a conditional reducer would
    // make the store shape depend on who is signed in.
    [adminApi.reducerPath]: adminApi.reducer,
    [enrichmentApi.reducerPath]: enrichmentApi.reducer,
    [workspaceApi.reducerPath]: workspaceApi.reducer,
    [uploadsApi.reducerPath]: uploadsApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [companyProfileApi.reducerPath]: companyProfileApi.reducer,
    [savedCompaniesApi.reducerPath]: savedCompaniesApi.reducer,
    [savedContactsApi.reducerPath]: savedContactsApi.reducer,
    [workflowApi.reducerPath]: workflowApi.reducer,
    [phoneWorkflowApi.reducerPath]: phoneWorkflowApi.reducer,
    [providersApi.reducerPath]: providersApi.reducer,
    // Lists / suppression
    [emailSuppressionApi.reducerPath]: emailSuppressionApi.reducer,
    [nameSuppressionApi.reducerPath]: nameSuppressionApi.reducer,
    [websiteSuppressionApi.reducerPath]: websiteSuppressionApi.reducer,
    [contactListApi.reducerPath]: contactListApi.reducer,
    // CRM integration
    [crmApi.reducerPath]: crmApi.reducer,
    [crmImportApi.reducerPath]: crmImportApi.reducer,
    [hubspotApi.reducerPath]: hubspotApi.reducer,
    [salesforceApi.reducerPath]: salesforceApi.reducer,
    // Custom data orders
    [customDataApi.reducerPath]: customDataApi.reducer,
});

/**
 * Resets EVERY slice to its initial state on logout, by handing
 * `combineReducers` no previous state at all - the same effect a hard page
 * reload has, without the reload. Covers every API automatically, including
 * ones added after this was written: there is nothing per-slice to remember,
 * which is what let the credit/entitlements/team caches go unreset for as
 * long as they did.
 *
 * `logout.fulfilled` only, not `.pending` - the server round trip clears the
 * httpOnly cookies first, and AuthGuard does not move `status` to
 * "unauthenticated" until this thunk resolves, so nothing observable renders
 * from the old state in between.
 */
const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: AnyAction) => {
  if (action.type === logout.fulfilled.type) {
    state = undefined;
  }
  return appReducer(state, action);
};

export const store = configureStore({
  // Cast preserved from the pre-refactor `reducer: {...} as any`: RootState
  // was already `any` before this file was touched, and removing the cast
  // here (rather than as a deliberate, separate change) surfaces PRE-EXISTING
  // type errors in unrelated files - PeopleFilters.tsx, Groups.tsx,
  // EmailSuppressionCreator.tsx - that a stale-cache-on-logout fix has no
  // business also fixing. Tightening RootState's type is worth doing on
  // purpose, separately, once those are triaged.
  reducer: rootReducer as any,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(companyApi.middleware)
      .concat(peopleApi.middleware)
      .concat(searchApi.middleware)
      .concat(exportApi.middleware)
      .concat(creditApi.middleware)
      .concat(entitlementsApi.middleware)
      .concat(teamApi.middleware)
      .concat(profileApi.middleware)
      .concat(pricingApi.middleware)
      .concat(dropdownApi.middleware)
      .concat(adminApi.middleware)
      .concat(enrichmentApi.middleware)
      .concat(workspaceApi.middleware)
      .concat(uploadsApi.middleware)
      .concat(notificationApi.middleware)
      .concat(companyProfileApi.middleware)
      .concat(savedCompaniesApi.middleware)
      .concat(savedContactsApi.middleware)
      .concat(workflowApi.middleware)
      .concat(phoneWorkflowApi.middleware)
      .concat(providersApi.middleware)
      .concat(emailSuppressionApi.middleware)
      .concat(nameSuppressionApi.middleware)
      .concat(websiteSuppressionApi.middleware)
      .concat(contactListApi.middleware)
      .concat(crmApi.middleware)
      .concat(crmImportApi.middleware)
      .concat(hubspotApi.middleware)
      .concat(salesforceApi.middleware)
      .concat(customDataApi.middleware)
});

// Setup listeners for RTK Query (enables automatic refetching)
if (typeof window !== "undefined") {
  setupListeners(store.dispatch);
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch<AppDispatch>();
